import React, { useEffect, useRef, useState } from 'react';
import { CancelToken, isCancel } from 'axios';

import { Autocomplete, TextField, Grid, Typography } from '@mui/material';

import BackgroundImage from '../../BackgroundImage';
import { getSearchApiAxiosInstance } from '../../../utils/network';
import { playerSearchPropTypes, defaultPlayerSearchPropTypes } from '../../../utils/proptypes';
import { useDebounce } from '../../../hooks/useDebounce';
import { buildPlayerSearchParams } from '../../../utils/playerSearch';

const PlayerSearch = ({ isProPlayerSearch = false, searchMembersOnly = false, showPickleballContent = false, showTennisContent = false, onPlayerSelected, selectedPlayers = [], label, isRequired = false, pin, distance, showUtr = false, noOptionsText }) => {
    const cancelRef = useRef(null);
    const resultLimit = 100;
    const [query, setQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const searchQuery = useDebounce(query, 500);
    const axiosInstance = getSearchApiAxiosInstance();

    const clearState = () => {
        setIsLoading(false);
        setSearchResults([]);
        setQuery('');
    };

    useEffect(() => {
        const getSearchResults = async () => {
            setIsLoading(true);
            const params = buildPlayerSearchParams({
                query: searchQuery,
                resultLimit,
                isProPlayerSearch,
                searchMembersOnly,
                showPickleballContent,
                showTennisContent,
                pin,
                distance,
            });

            const cancel = cancelRef.current;
            if (cancel) {
                cancel();
            }

            const response = await axiosInstance
                .get('v2/search/players', {
                    cancelToken: new CancelToken((c) => {
                        cancelRef.current = c;
                    }),
                    params,
                })
                .catch((error) => {
                    if (!isCancel(error)) {
                        setIsLoading(false);
                    }
                });

            if (response?.data) {
                const { hits } = response.data;
                const filteredHits = hits.filter((hit) => {
                    return selectedPlayers.every((player) => player.memberId !== hit.source.memberId);
                });
                const results = filteredHits;
                setSearchResults(results);
                setIsLoading(false);
            }
        };

        if (searchQuery.length > 0) {
            getSearchResults();
        }
    }, [searchQuery, pin, distance]);

    const updateStateForQuery = (queryString) => {
        if (queryString?.length > 0) {
            setQuery(queryString);
            setSearchResults([]);
        } else {
            setQuery('');
            setSearchResults([]);
        }
    };

    const handleOnInputChange = (value) => {
        updateStateForQuery(value);
    };

    const handleOnInputPaste = (event) => {
        const { target } = event;
        setTimeout(() => {
            const queryString = target.value.trim();
            updateStateForQuery(queryString);
        }, 200);
    };

    const handleOnChange = (player) => {
        onPlayerSelected(player);
        clearState();
    };

    return (
        <Autocomplete
            id="player-search"
            filterOptions={(x) => x}
            onPaste={handleOnInputPaste}
            options={searchResults}
            loading={isLoading}
            noOptionsText={noOptionsText}
            getOptionLabel={(option) => (option?.source?.displayName ? option.source.displayName : '')}
            isOptionEqualToValue={(option, value) => option?.source?.id === value.source.id}
            onInputChange={(_, value, reason) => {
                if ((value || value === '') && reason === 'input') {
                    handleOnInputChange(value);
                }
            }}
            onChange={(_, newValue) => {
                if (newValue) {
                    handleOnChange(newValue.source);
                }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    required={isRequired}
                    label={label}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                    }}
                />
            )}
            renderOption={(optionProps, option) => {
                const { playerProfileImages, displayName, location, singlesUtr } = option?.source || {};
                return (
                    <li {...optionProps} key={option?.source.id}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <BackgroundImage
                                    baseUrl="/v1/player/"
                                    defaultImage="/img/v2/icons/icon-utr-logo.svg"
                                    images={playerProfileImages}
                                    imageType="profile"
                                    extraCls="playerThumbnailImage"
                                />
                            </Grid>
                            <Grid item>
                                <Typography>{displayName}</Typography>
                                {showUtr && (
                                    <Typography sx={{ color: 'gray', size: 12 }}>{`UTR ${singlesUtr || '-'}`}</Typography>
                                )}
                                <Typography sx={{ color: 'gray', size: 12 }}>{location?.display}</Typography>
                            </Grid>
                        </Grid>
                    </li>
                );
            }}
        />
    );
};

PlayerSearch.propTypes = {
    ...playerSearchPropTypes
};

PlayerSearch.defaultProps = {
    ...defaultPlayerSearchPropTypes
};

export default PlayerSearch;
